import { useNavigate } from 'react-router-dom';
import { useUserJourney } from '../../hooks/useUserJourney';
import { CATEGORY_LABELS, type SystemDefinition } from '../../types/systems';

function SystemCard({ system, explored, onClick }: {
  system:   SystemDefinition;
  explored: boolean;
  onClick:  () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`text-left p-4 rounded-sm border transition-all ${
        explored
          ? 'border-[#C9A84C]/40 bg-[#C9A84C]/5 hover:bg-[#C9A84C]/10'
          : 'border-[#F5ECD7]/10 hover:border-[#C9A84C]/30'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">{system.icon}</span>
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-sm font-medium ${explored ? 'text-[#C9A84C]' : 'text-[#F5ECD7]/80'}`}>
              {system.label}
            </span>
            {explored && <span className="text-[#C9A84C] text-xs">✦</span>}
            {system.isPremium && !explored && (
              <span className="text-[#F5ECD7]/30 text-xs border border-[#F5ECD7]/20 px-1 rounded-sm">PRO</span>
            )}
          </div>
          <p className="text-[#F5ECD7]/50 text-xs leading-relaxed">{system.description}</p>
        </div>
      </div>
    </button>
  );
}

export function JourneyDashboard() {
  const navigate = useNavigate();
  const { systems, exploredCount, totalCount, percentage, recommended, loading } = useUserJourney();

  if (loading) {
    return (
      <div className="border border-[#C9A84C]/20 rounded-sm p-6">
        <div className="text-[#F5ECD7]/40 text-sm">Carregando sua jornada…</div>
      </div>
    );
  }

  // Object.groupBy is ES2024+, use it when available, fall back to reduce
  const groupBy = (Object as unknown as { groupBy?: <T>(arr: T[], fn: (v: T) => string) => Record<string, T[]> }).groupBy;
  const byCategory = groupBy
    ? groupBy(systems, s => s.definition.category)
    : systems.reduce<Record<string, typeof systems>>((acc, s) => {
        (acc[s.definition.category] ||= []).push(s);
        return acc;
      }, {});

  return (
    <div className="border border-[#C9A84C]/20 rounded-sm p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-[#C9A84C] text-sm uppercase tracking-[2px] mb-1">
          ✦ Sua Jornada de Descoberta
        </h2>
        <p className="text-[#F5ECD7]/50 text-xs">
          {exploredCount} de {totalCount} sistemas explorados
        </p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between mb-2">
          <span className="text-[#F5ECD7]/60 text-xs">Progresso</span>
          <span className="text-[#C9A84C] text-xs font-medium">{percentage}%</span>
        </div>
        <div className="h-2 bg-[#C9A84C]/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#C9A84C] rounded-full transition-all duration-700"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Recommendations */}
      {recommended.length > 0 && (
        <div>
          <h3 className="text-[#F5ECD7]/60 text-xs uppercase tracking-wider mb-3">
            Experimente a seguir
          </h3>
          <div className="flex flex-wrap gap-2">
            {recommended.map(sys => (
              <button
                key={sys.key}
                onClick={() => navigate(sys.route)}
                className="flex items-center gap-2 px-3 py-2 border border-[#C9A84C]/30 text-[#C9A84C] text-xs rounded-sm hover:bg-[#C9A84C]/10 transition-colors"
              >
                <span>{sys.icon}</span>
                <span>{sys.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category grid */}
      {(Object.entries(byCategory) as [string, typeof systems][]).map(([cat, catSystems]) => (
        <div key={cat}>
          <h3 className="text-[#F5ECD7]/50 text-xs uppercase tracking-wider mb-3">
            {CATEGORY_LABELS[cat as keyof typeof CATEGORY_LABELS] ?? cat}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {catSystems.map(sp => (
              <SystemCard
                key={sp.definition.key}
                system={sp.definition}
                explored={sp.isExplored}
                onClick={() => navigate(sp.definition.route)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
